const MENTION_TOKEN_RE = /(?<![\p{L}\p{N}_@./+-])@([a-z0-9][a-z0-9-]*)(?![\p{L}\p{N}_-])/gu;

export interface ReferenceBinding {
  token: string;
  kind: "element" | "asset";
  id: string;
  updated_at?: string;
}

export interface DirectImageReference {
  asset_id: string;
  type: "image";
  name: string;
  storage_key?: string;
  thumbnail_storage_key?: string | null;
}

/** Only images attached to this draft enter the direct-image mention picker. */
export function directImageReferences(params: Record<string, unknown>): DirectImageReference[] {
  const output: DirectImageReference[] = [];
  for (const field of ["image", "end_image", "reference_images"]) {
    const source = params[field];
    for (const value of Array.isArray(source) ? source : source ? [source] : []) {
      if (!value || typeof value !== "object") continue;
      const id = typeof value.asset_id === "string" ? value.asset_id : typeof value.id === "string" ? value.id : null;
      if (!id || (value.type && value.type !== "image") || output.some((item) => item.asset_id === id)) continue;
      output.push({ asset_id: id, type: "image", name: typeof value.name === "string" ? value.name : `Image ${output.length+1}`,
        storage_key: typeof value.storage_key === "string" ? value.storage_key : undefined,
        thumbnail_storage_key: typeof value.thumbnail_storage_key === "string" ? value.thumbnail_storage_key : undefined });
    }
  }
  return output;
}

/** Removing/replacing a direct image also removes its explicit text bindings. */
export function updateDraftParam(params: Record<string, unknown>, name: string, value: unknown): Record<string, unknown> {
  const next = { ...params, [name]: value };
  if (!["image", "end_image", "reference_images"].includes(name)) return next;
  const retained = new Set(directImageReferences(next).map((image) => image.asset_id));
  const removed = new Set(directImageReferences(params).filter((image) => !retained.has(image.asset_id)).map((image) => image.asset_id));
  const bindings = referenceBindings(params);
  const discarded = bindings.filter((item) => item.kind === "asset" && removed.has(item.id));
  if (!discarded.length) return next;
  next.reference_bindings = bindings.filter((item) => !discarded.includes(item));
  for (const field of MENTION_TEXT_FIELDS) {
    if (typeof next[field] !== "string") continue;
    next[field] = discarded.reduce((text, item) => removeMentionToken(text, item.token.slice(1)), next[field] as string);
  }
  return next;
}

export function referenceBindings(params: Record<string, unknown>): ReferenceBinding[] {
  if (!Array.isArray(params.reference_bindings)) return [];
  return params.reference_bindings.filter((value): value is ReferenceBinding => Boolean(
    value && typeof value === "object" && typeof value.token === "string" && typeof value.id === "string"
    && (value.kind === "element" || value.kind === "asset")
  ));
}

/** Allocate a stable alias when an admin selects two owners' identically named Elements. */
export function bindReference(params: Record<string, unknown>, source: ReferenceBinding): ReferenceBinding {
  const existing = referenceBindings(params);
  const same = existing.find((item) => item.kind === source.kind && item.id === source.id);
  if (same) return { ...same, ...source, token: same.token };
  const tokens = new Set(existing.map((item) => item.token));
  let token = source.token;
  for (let suffix = 2; tokens.has(token); suffix += 1) token = `${source.token}-${suffix}`;
  return { ...source, token };
}

export function pruneReferenceBindings(params: Record<string, unknown>): ReferenceBinding[] {
  const tokens = new Set(MENTION_TEXT_FIELDS.flatMap((key) => listMentionSlugs(typeof params[key] === "string" ? params[key] as string : "")).map((slug) => `@${slug}`));
  return referenceBindings(params).filter((binding) => tokens.has(binding.token));
}

export const MENTION_TEXT_FIELDS = [
  "prompt",
  "subject",
  "action",
  "performance",
  "dialogue",
  "environment",
  "camera",
  "lighting",
] as const;

export type MentionTextField = (typeof MENTION_TEXT_FIELDS)[number];

export interface MentionMatch {
  start: number;
  end: number;
  slug: string;
}

export interface ActiveMentionToken {
  start: number;
  end: number;
  query: string;
}

export function findMentionMatches(text: string): MentionMatch[] {
  const matches: MentionMatch[] = [];
  MENTION_TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN_RE.exec(text)) !== null) {
    matches.push({ start: match.index, end: match.index + match[0].length, slug: match[1] });
  }
  return matches;
}

export function listMentionSlugs(text: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const match of findMentionMatches(text)) {
    if (!seen.has(match.slug)) {
      seen.add(match.slug);
      ordered.push(match.slug);
    }
  }
  return ordered;
}

function collapseSeam(text: string, at: number): string {
  let start = at;
  while (start > 0 && /[ \t]/.test(text[start - 1])) start -= 1;
  let end = at;
  while (end < text.length && /[ \t]/.test(text[end])) end += 1;
  if (start === end) return text;
  const before = text.slice(0, start);
  const after = text.slice(end);
  const beforeChar = before.slice(-1);
  const afterChar = after.slice(0, 1);
  const atBoundary = !beforeChar || beforeChar === "\n" || !afterChar || afterChar === "\n";
  return before + (atBoundary ? "" : " ") + after;
}

export function removeMentionToken(text: string, slug: string): string {
  const matches = findMentionMatches(text).filter((match) => match.slug === slug);
  if (!matches.length) return text;
  let next = text;
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const { start, end } = matches[i];
    next = collapseSeam(next.slice(0, start) + next.slice(end), start);
  }
  return next;
}

export function findActiveMentionToken(text: string, caret: number): ActiveMentionToken | null {
  if (caret < 0 || caret > text.length) return null;
  const uptoCaret = text.slice(0, caret);
  const start = uptoCaret.lastIndexOf("@");
  if (start === -1) return null;
  const before = start === 0 ? "" : text[start - 1];
  if (before && /[\p{L}\p{N}_@./+-]/u.test(before)) return null;
  const query = uptoCaret.slice(start + 1);
  if (!/^[a-z0-9-]*$/i.test(query)) return null;
  let end = caret;
  while (end < text.length && /[a-z0-9-]/i.test(text[end])) end += 1;
  return { start, end, query };
}

export function replaceMentionToken(
  text: string,
  token: { start: number; end: number },
  slug: string
): { text: string; caret: number } {
  const after = text.slice(token.end);
  const needsSpace = after.length === 0 || !/^\s/.test(after);
  const inserted = `@${slug}${needsSpace ? " " : ""}`;
  const nextText = text.slice(0, token.start) + inserted + after;
  return { text: nextText, caret: token.start + inserted.length };
}
